// src/hooks/useReownWallet.js
import { useState, useEffect, useCallback } from 'react';
import { useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitProvider } from '@reown/appkit/react';
import { ethers } from 'ethers';

export const useReownWallet = () => {
  const { open, close } = useAppKit();
  const { address, isConnected, status } = useAppKitAccount();
  const { chainId, switchNetwork } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider('eip155');
  
  const [provider, setProvider] = useState(null);

  // Create ethers v5 provider when wallet is connected
  useEffect(() => {
    if (walletProvider && isConnected) {
      try {
        const ethersProvider = new ethers.providers.Web3Provider(walletProvider);
        setProvider(ethersProvider);
        console.log('✅ Ethers provider created');
      } catch (error) {
        console.error('❌ Failed to create ethers provider:', error);
        setProvider(null);
      }
    } else {
      setProvider(null);
    }
  }, [walletProvider, isConnected]);

  // Connect wallet
  const connect = useCallback(async () => {
    try {
      await open();
    } catch (error) {
      console.error('Connection error:', error);
      throw error;
    }
  }, [open]);

  // Disconnect wallet
  const disconnect = useCallback(async () => {
    try {
      await close();
    } catch (error) {
      console.error('Disconnect error:', error);
      throw error;
    }
  }, [close]);

  // Get current account (normalized to lowercase)
  const getAccount = useCallback(() => {
    return address ? address.toLowerCase() : null;
  }, [address]);

  // Get current chain ID as number
  const getChainId = useCallback(() => {
    return chainId ? Number(chainId) : null;
  }, [chainId]);

  // Switch to a specific chain using Reown's switchNetwork
  const switchToNetwork = useCallback(async (targetChainId) => {
    if (!isConnected) {
      throw new Error('Wallet not connected');
    }

    try {
      console.log(`🔄 Switching to chain ${targetChainId}...`);
      
      // Use Reown's built-in network switcher
      await switchNetwork(targetChainId);
      
      console.log('✅ Network switch initiated');
      return true;
    } catch (error) {
      console.error('❌ Network switch error:', error);
      
      // If switchNetwork fails, fall back to opening the network modal
      if (error.code === 4902 || error.message?.includes('Unrecognized chain')) {
        console.log('⚠️ Chain not recognized, opening network selector...');
        await open({ view: 'Networks' });
      }
      
      throw error;
    }
  }, [switchNetwork, isConnected, open]);

  return {
    // State
    address: address ? address.toLowerCase() : null,
    chainId: chainId ? Number(chainId) : null,
    isConnected,
    isConnecting: status === 'connecting',
    provider,
    
    // Methods
    connect,
    disconnect,
    getAccount,
    getChainId,
    switchNetwork: switchToNetwork,
    openModal: open,
    openNetworkModal: () => open({ view: 'Networks' })
  };
};